import {IModel} from "makerjs";
import {sprintf} from "sprintf-js";

import isEmpty from "lodash/isEmpty";
import isUndefined from "lodash/isUndefined";

import Length from "../../../../source/js/shared/models/Geometry/Length";
import { isModelInsideParent } from "@/functions/makerjs";
import { toNumber } from "@/functions/numbers";

import DrillHole from "@/Models/DrillHole";
import {ValidatorInterface} from "@/Models/Validators/ValidatorInterface";
import RadialDrillHoleValidatorSet from "@/Models/Validators/Sets/RadialDrillHoleValidatorSet";
import GridDrillHoleValidatorSet from "@/Models/Validators/Sets/GridDrillHoleValidatorSet";
import CustomDrillHoleValidatorSet from "@/Models/Validators/Sets/CustomDrillHoleValidatorSet";
import EdgeDrillHoleValidatorSet from "@/Models/Validators/Sets/EdgeDrillHoleValidatorSet";
import NullValidatorSet from "@/Models/Validators/Sets/NullValidatorSet";
import CustomDrillHole from "@/Models/CustomDrillHole";
import RadialDrillHoles from "@/Models/RadialDrillHoles";
import GridDrillHoles from "@/Models/GridDrillHoles";
import EdgeDrillHoles from "@/Models/EdgeDrillHoles";
import PresetDrillHoles from "@/Models/PresetDrillHoles";
import {
	getValidationErrors,
	getValidationMessageForField,
	hasValidationErrors,
} from "@/utilities/Validator";
import ShapeDrillHoleGeneratorFactory from "@/Models/ShapeDrillHoleGeneratorFactory";
import PresetDrillHoleValidatorSet from "@/Models/Validators/Sets/PresetDrillHoleValidatorSet";
import Invalid from "@/Models/DrillHoleGenerators/Invalid";
import { ShapeModelCombineDecorator } from "@/utilities/decorators/ShapeModelDecorator";
import { CustomDrillHoleDecorator } from "@/utilities/decorators/PreviewModelDecorator";
import { StepTypes } from "@/types/step";

/**
 * Calculates the grid spacing based on the size of the axis, desired edge offset, hole diameter and amount of holes.
 * The spacing is determined based off of the center of each hole to ensure an equal distance.
 *
 * All sizes are presumed to be of a single unit (i.e. mm, cm etc.).
 *
 * @param {number} size The size of the axis.
 * @param {number} offset The offset from the edge of the containing shape.
 * @param {number} diameter The diameter of the hole that needs to be drilled.
 * @param {number} length The amount of holes to add to the grid.
 *
 * @returns {number} The distance per hole.
 */
export function calculateGridSpacing( size: number, offset: number, diameter: number, length: number ): number {
	const workArea = size - ( offset * 2 );
	const spaceToUse = ( length > 1 ) ? length - 1 : 1;

	return ( workArea - ( length * diameter ) ) / spaceToUse;
}

export default {
	state: (): object => ( {
		defaultDrillHoleDiameter: toNumber( window.measurementSelectorVariables.pricingGroup.other_settings.default_drillhole_diameter ),
		drillHoleDiameter: toNumber( window.measurementSelectorVariables.pricingGroup.other_settings.default_drillhole_diameter ),
		minDrillHoleDiameter: toNumber( window.measurementSelectorVariables.pricingGroup.other_settings.min_drillhole_diameter ?? 1 ),
		drillHoleOffset: 30,
		drillHoleRadialAmount: 10,
		drillHoleRadialOffsetX: 30,
		drillHoleRadialOffsetY: 30,
		drillHoleRadialDiameter: 5,
		drillHoleHorizontalAmount: 5,
		drillHoleVerticalAmount: 5,
		customDrillHoles: [],
		currentCustomDrillhole: 0,
		drillOption: window.measurementSelectorVariables.pricingGroup.other_settings.drill_hole_presets_active ? 'preset' : 'none',
		drillHoleEdgeType: "corners",
		drillHoleEdgeHoles: [],
		defaultDrillHoleDimensions: true,
		drillHoleEdgeMaxDistance: window.measurementSelectorVariables.drillHoleSettings.presetGroup.drill_hole_max_distance,
		drillHoleEdgeTopBottom: window.measurementSelectorVariables.drillHoleSettings.presetGroup.drill_hole_distance_to_edge_top_bottom,
		drillHoleEdgeLeftRight: window.measurementSelectorVariables.drillHoleSettings.presetGroup.drill_hole_distance_to_edge_left_right,
		drillHoleStartingPoint: window.measurementSelectorVariables.drillHoleSettings.presetGroup.drill_hole_starting_point,
	} ),
	mutations: {
		updateDrillHoleRadialAmount: ( state, value ): number => state.drillHoleRadialAmount = value,
		updateDrillHoleRadialOffsetX: ( state, value ): number => state.drillHoleRadialOffsetX = value,
		updateDrillHoleRadialOffsetY: ( state, value ): number => state.drillHoleRadialOffsetY = value,
		updateDrillHoleRadialDiameter: ( state, value ): number => state.drillHoleRadialDiameter = value,
		updateDrillHoleHorizontalAmount: ( state, value ): number => state.drillHoleHorizontalAmount = value,
		updateDrillHoleVerticalAmount: ( state, value ): number => state.drillHoleVerticalAmount = value,
		updateDrillHoleDiameter: ( state, value ): number => state.drillHoleDiameter = value,
		updateDrillHoleOffset: ( state, value ): number => state.drillHoleOffset = value,
		updateCurrentCustomDrillhole: ( state, drillhole: number ): number => state.currentCustomDrillhole = drillhole,
		addCustomHole: ( state, value: CustomDrillHole ): void => state.customDrillHoles.push( value ),
		removeCustomHole: ( state, index: number ): void => state.customDrillHoles.splice(index, 1),
		resetCustomHoles: ( state ): CustomDrillHole[] => state.customDrillHoles = [],
		updateCustomDrillholes: ( state, value: CustomDrillHole[] ): CustomDrillHole[] => state.customDrillHoles = value,
		updateDrillOption: ( state, drillOption ): void => {
			state.drillOption = drillOption;
			state.defaultDrillHoleDimensions = true;

			if ( ! drillOption || drillOption.length === 0 ) {
				return;
			}

			window?.dataLayer?.push( {
				event: 'measurements_selector',
				eventCategory: 'Drill options: ' + document.title,
				eventAction: 'Step 3. Choose drill options',
				eventLabel: drillOption,
			} );
		},
		updateDrillHoleEdgeType( state, drillHoleEdgeType ): void {
			state.drillHoleEdgeType = drillHoleEdgeType;

			if ( !drillHoleEdgeType || 0 === drillHoleEdgeType.length ) {
				return;
			}

			window?.dataLayer?.push( {
				event: 'measurements_selector',
				eventCategory: 'Drill hole edge type: ' + document.title,
				eventAction: 'Step 3. Choose drill hole edge type',
				eventLabel: drillHoleEdgeType,
			} );
		},
		updateEdgeHoles: ( state, edgeHoles ): void => state.drillHoleEdgeHoles = edgeHoles,
		updateUsingDefaultDrillHoleDimensions: ( state, usesDefaults ): void => state.defaultDrillHoleDimensions = usesDefaults,
		updateDrillHoleEdgeTopBottom: ( state, distance ): void => state.drillHoleEdgeTopBottom = distance,
		updateDrillHoleEdgeLeftRight: ( state, distance ): void => state.drillHoleEdgeLeftRight = distance,
	},
	getters: {
		drillOption: ( state ): string => state.drillOption,
		drillHoleEdgeType: ( state ): string => state.drillHoleEdgeType,
		drillHoleEdgeHoles: ( state ): number[] => state.drillHoleEdgeHoles,
		radialDrillHoles: ( state, getters ): RadialDrillHoles => {
			if ( getters.drillOption !== 'radial' ) {
				return null;
			}
			try {

				return new RadialDrillHoles(
					getters.drillHoleRadialOffsetX,
					getters.drillHoleRadialOffsetY,
					getters.drillHoleDiameter,
					getters.drillHoleRadialDiameter,
					getters.drillHoleRadialAmount,
					getters.enclosingRectangle
				);
			} catch ( e ) {

				// Intentionally left empty.
			}
			return null;
		},
		gridDrillHoles: ( state, getters ): GridDrillHoles => {
			if ( getters.drillOption !== 'grid' ) {
				return null;
			}

			try {
				return new GridDrillHoles(
					getters.drillHoleDiameter,
					getters.drillHoleOffset,
					getters.drillHoleHorizontalAmount,
					getters.drillHoleVerticalAmount,
					getters.enclosingRectangle
				);
			} catch ( e ) {

				// Intentionally left empty.
			}
			return null;
		},
		edgeDrillHoles: ( state, getters ): EdgeDrillHoles => {
			if (getters.drillOption !== 'edges') {
				return null;
			}

			if ( getters.drillHoleEdgeType === null ) {
				return null;
			}

			try {
				return new EdgeDrillHoles(
					getters.shape,
					getters.model,
					getters.drillHoleDiameter,
					getters.drillHoleOffset,
					getters.drillHoleEdgeType,
					getters.drillHoleEdgeHoles
				);
			} catch ( e ) {

				// Intentionally left empty.
			}
			return null;
		},
		presetDrillHoles: ( state, getters ): PresetDrillHoles => {
			if ( getters.drillOption !== 'preset' ) {
				return null;
			}

			try {
				return new PresetDrillHoles(
					getters.shape,
					getters.model,
					getters.primitives,
					getters.drillHoleDiameter,
					getters.drillHoleEdgeMaxDistance,
					getters.drillHoleEdgeTopBottom,
					getters.drillHoleEdgeLeftRight,
					getters.drillHoleStartingPoint
				);
			} catch ( e ) {

				// Intentionally left empty.
			}
			return null;
		},
		customDrillHoles: ( state ): CustomDrillHole[] => state.customDrillHoles,
		getCustomDrillHoles: ( state, getters ): DrillHole[] => {
			const drillHoles = [];

			for ( const hole of getters.customDrillHoles ) {
				drillHoles.push(
					new DrillHole(
						new Length( hole.x ),
						new Length( hole.y ),
						new Length( hole.diameter )
					)
				);
			}

			return drillHoles;
		},
		getDrillHoleValidators: function ( state, getters ): ValidatorInterface[] {
			const completeModel = getters.getExportableModel;

			if ( isEmpty( getters.shape ) || isEmpty( completeModel.models ) || ( getters.drillOption === 'edges' && isEmpty( getters.edgeDrillHoles ) ) ) {
				return [];
			}

			const minHoleDiameter = new Length( toNumber( window.measurementSelectorVariables.pricingGroup.other_settings.min_drillhole_diameter ?? 1 ) );
			const { drillHoles } = completeModel.models;

			const baseShapeModelDecorator = getters.getBaseShapeModelDecorator;
			const combinedShape = (new ShapeModelCombineDecorator(new CustomDrillHoleDecorator(baseShapeModelDecorator, getters.getCustomDrillHoles)).getModel());
			const baseModel = combinedShape.models.shapeModel.models.baseModel;

			let validatorSet;

			switch ( getters.drillOption ) {
				case 'radial':
					validatorSet = new RadialDrillHoleValidatorSet( minHoleDiameter, state, getters.shape, getters.parsedDimensions, drillHoles, baseModel, getters.getGeneratedDrillHoleInstance);
					break;
				case 'grid':
					validatorSet = new GridDrillHoleValidatorSet( minHoleDiameter, state, drillHoles, baseModel );
					break;
				case 'custom':
					validatorSet = new CustomDrillHoleValidatorSet( minHoleDiameter, state, getters.customDrillHoles, baseShapeModelDecorator.getModel(), combinedShape );
					break;
				case 'edges':
					validatorSet = new EdgeDrillHoleValidatorSet( minHoleDiameter, state, getters.drillHoleConfig, drillHoles, baseModel );
					break;
				case 'preset':
					validatorSet = new PresetDrillHoleValidatorSet( minHoleDiameter, state );
					break;
				default:
					validatorSet = new NullValidatorSet();
					break;
			}

			return validatorSet.getValidators();
		},
		getDrillHoleValidatorErrors: function ( state, getters ): ValidatorInterface[] {
			return getValidationErrors( getters.getDrillHoleValidators );
		},
		hasDrillHoleValidatorErrors: ( state, getters ): boolean => { return hasValidationErrors( getters.getDrillHoleValidators ); },
		drillHoleConfig: ( state, getters, rootstate ): object => {
			const data = {
				type: getters.drillOption
			};
			rootstate.measurements.forceRefresh;

			if ( getters.drillOption === 'none' ) {
				return data;
			}

			if ( getters.drillOption === 'edges' ) {
				data[ 'edgeType' ] = getters.drillHoleEdgeType;
				data[ 'offset' ] = getters.drillHoleOffset.in_mm();
				data[ 'diameter' ] = getters.drillHoleDiameter.in_mm();
				if( getters.drillHoleEdgeType === 'around' ) {
					data[ 'edges' ] = getters.drillHoleEdgeHoles;
				}
			}

			if ( getters.drillOption === 'radial' ) {
				data[ 'amount' ] = getters.drillHoleRadialAmount;
				data[ 'position' ] = {};
				data[ 'position' ][ 'left' ] = getters.drillHoleRadialOffsetX.in_mm();
				data[ 'position' ][ 'bottom' ] = getters.drillHoleRadialOffsetY.in_mm();
				data[ 'radialDiameter' ] = getters.drillHoleRadialDiameter.in_mm();
				data[ 'diameter' ] = getters.drillHoleDiameter.in_mm();
			}

			if ( getters.drillOption === 'grid' ) {
				data[ 'amountHorizontal' ] = getters.drillHoleHorizontalAmount;
				data[ 'amountVertical' ] = getters.drillHoleVerticalAmount;
				data[ 'offset' ] = getters.drillHoleOffset.in_mm();
				data[ 'diameter' ] = getters.drillHoleDiameter.in_mm();
			}

			if ( getters.drillOption === 'custom' ) {
				data[ 'settings' ] = getters.customDrillHoles;
			}

			if ( getters.drillOption === 'preset' ) {
				data[ 'edge_left_right' ] = getters.drillHoleEdgeLeftRight.in_mm();
				data[ 'edge_top_bottom' ] = getters.drillHoleEdgeTopBottom.in_mm();
				data[ 'max_distance' ] = getters.drillHoleEdgeMaxDistance.in_mm();
				data[ 'diameter' ] = getters.drillHoleDiameter.in_mm();
				data[ 'starting_point' ] = getters.drillHoleStartingPoint;
			}

			return data;
		},
		getGeneratedDrillHoleInstance( state, getters ): [] {
			let instance = null;

			if ( getters.edgeDrillHoles ) {
				instance = getters.edgeDrillHoles;
			}

			if ( getters.radialDrillHoles ) {
				instance = getters.radialDrillHoles;
			}

			if ( getters.gridDrillHoles ) {
				instance = getters.gridDrillHoles;
			}

			if ( getters.presetDrillHoles ) {
				instance = getters.presetDrillHoles;
			}

			return instance;
		},
		getGeneratedDrillHoles( state, getters, rootstate ): Function {
			return ( isPreview = true ): IModel|null => {
				const instance = getters.getGeneratedDrillHoleInstance;
				let model = null;

				// This is currently necessary to ensure drill holes going around the shape are properly rendered as this isn't reactive.
				rootstate.measurements.forceRefresh;

				if ( instance !== null ) {
					model = instance.getAsModel();

					if ( ( model.type === 'grid-drillHole' && isUndefined( model.models ) ) || ( model.type === 'radial-drillHole' && isEmpty( model.paths ) ) ) {
						return null;
					}

					if ( isPreview ) {
						model.layer = 'preview-drillhole';

						if( getters.currentStepName === StepTypes.drillholes && getters.drillOption !== 'none') {
							model.layer = 'active-drillhole';
						}
					}
				}

				return model;
			};
		},
		isDrillHoleValid( state, getters ) {
			return ( field, index?: number ): boolean => {
				if ( ( field === 'drillHoleOffsetX' || field === 'drillHoleOffsetY' ) && index ) {
					field = `${field}_${index}`;
				}

				return ! hasValidationErrors( getters.getDrillHoleValidators, field );
			};
		},
		getDrillHoleValidationMessage( state, getters ) {
			return ( field, index?: number ): string => {
				if ( ( field === 'drillHoleOffsetX' || field === 'drillHoleOffsetY' || field === 'drillHoleOffset' ) && index ) {
					field = `${field}_${index}`;
				}

				return getValidationMessageForField( getters.getDrillHoleValidators, field );
			};
		},
		currentCustomDrillhole: ( state ): string => state.currentCustomDrillhole,
		minimumDrillHoleDiameterLength: ( state ): Length => new Length( state.minDrillHoleDiameter ),
		minimumDrillHoleDiameter: ( state, getters ): number => getters.minimumDrillHoleDiameterLength.in_cm(),
		minimumDrillHoleDiameterLabel( state, getters ): string {
			return sprintf(
				window.measurementSelectorVariables.i18n.drillHoleConfiguration.diameter_drill_hole_min,
				Length.from_cm( getters.minimumDrillHoleDiameter ).in_cm_i18n()
			);
		},
		usesDefaultDrillHoleDimensions: ( state ): boolean => state.defaultDrillHoleDimensions,
		countEdges: (): Function => {
			return ( shape, model, offset: Length ): number => {
				try{
					const shapeDrillHoleGeneratorFactory = new ShapeDrillHoleGeneratorFactory();
					const shapeDrillHoleGenerator = shapeDrillHoleGeneratorFactory.getShapeDrillHoleGenerator( shape, model );

					if ( ! ( 'countEdges' in shapeDrillHoleGenerator ) ) {
						return 0;
					}

					return shapeDrillHoleGenerator.countEdges( offset );
				} catch( e ) {
					return 0;
				}
			};
		},
		drillHoleDiameter: ( state ): Length => new Length( state.drillHoleDiameter ),
		drillHoleOffset: ( state ): Length => new Length( state.drillHoleOffset ),
		drillHoleEdgeMaxDistance: ( state ): Length => new Length( state.drillHoleEdgeMaxDistance ),
		drillHoleEdgeTopBottom: ( state ): Length => new Length( state.drillHoleEdgeTopBottom ),
		drillHoleEdgeLeftRight: ( state ): Length => new Length( state.drillHoleEdgeLeftRight ),
		drillHoleStartingPoint: ( state ): Length => new Length( state.drillHoleStartingPoint ),
		drillHoleRadialAmount: ( state ): number => Math.max(1, state.drillHoleRadialAmount),
		drillHoleRadialDiameter: ( state ): Length => new Length( state.drillHoleRadialDiameter ),
		drillHoleRadialOffsetX: ( state ): Length => new Length( state.drillHoleRadialOffsetX ),
		drillHoleRadialOffsetY: ( state ): Length => new Length( state.drillHoleRadialOffsetY ),
		drillHoleHorizontalAmount: ( state ): number => Math.max(1, state.drillHoleHorizontalAmount),
		drillHoleVerticalAmount: ( state ): number => Math.max(1, state.drillHoleVerticalAmount),
		drillHoleTypeEnabled: ( state, getters ): Function => {
			return ( type: string ): boolean => {
				const shapeDrillHoleGeneratorFactory = new ShapeDrillHoleGeneratorFactory();
				const shapeDrillHoleGenerator = shapeDrillHoleGeneratorFactory.getShapeDrillHoleGenerator( getters.shape, getters.model );

				const options = {
					'corners': 'getCorners' in shapeDrillHoleGenerator,
					'around': 'getAround' in shapeDrillHoleGenerator,
					'top_bottom': false,
					'left_right': false
				};

				return ( ! isUndefined( options[ type ] ) && options[ type ] );
			};
		},
		getEdgeCount: ( state, getters ): number => {
			try {
				const shapeDrillHoleGenerator = (new ShapeDrillHoleGeneratorFactory()).getShapeDrillHoleGenerator( getters.shape, getters.model );
				const offset = getters.drillHoleOffset;

				if ( shapeDrillHoleGenerator instanceof Invalid ) {
					return 0;
				}

				return shapeDrillHoleGenerator.countEdges(offset);
			} catch(e) {
				return 0;
			}
		},
	},
	actions: {
		async updateDrillHolesFromConfig( { commit, state, dispatch }, drillholes ): Promise<void> {
			commit( 'updateDrillOption', drillholes.type );
			commit( 'updateDrillHoleDiameter', drillholes.diameter ?? state.defaultDrillHoleDiameter );
			commit( 'updateUsingDefaultDrillHoleDimensions', false );

			switch ( drillholes.type ) {
				case 'edges':
					dispatch( 'updateEdgeDrillHolesFromConfig', drillholes );

					break;
				case 'grid':
					dispatch( 'updateGridDrillHolesFromConfig', drillholes );

					break;
				case 'custom':

					// Ensure that the custom drill holes have a diameter set.
					if (drillholes.type === 'custom' && Array.isArray(drillholes.settings)) {
						drillholes.settings = drillholes.settings.map(hole => ({
							...hole,
							diameter: hole.diameter ?? state.defaultDrillHoleDiameter
						}));
					}

					commit( 'updateCustomDrillholes', drillholes.settings );
					break;
				case 'radial':
					dispatch( 'updateRadialDrillHolesFromConfig', drillholes );

					break;
				case 'preset':
					commit( 'updateDrillHoleEdgeTopBottom', drillholes.edge_top_bottom );
					commit( 'updateDrillHoleEdgeLeftRight', drillholes.edge_left_right );

					break;
				default:
					break;
			}
		},
		async updateRadialDrillHolesFromConfig( { commit, state }, drillholes ): Promise<void> {
			commit( 'updateDrillHoleRadialAmount', drillholes.amount );
			commit( 'updateDrillHoleRadialOffsetX', drillholes.position.left );
			commit( 'updateDrillHoleRadialOffsetY', drillholes.position.bottom );
			commit( 'updateDrillHoleRadialDiameter', drillholes.radialDiameter );
		},
		async updateGridDrillHolesFromConfig( { commit, state }, drillholes ): Promise<void> {
			commit( 'updateDrillHoleHorizontalAmount', drillholes.amountHorizontal );
			commit( 'updateDrillHoleVerticalAmount', drillholes.amountVertical );
			commit( 'updateDrillHoleOffset', drillholes.offset );
		},
		async updateEdgeDrillHolesFromConfig( { commit, state }, drillholes ): Promise<void> {
			commit( 'updateDrillHoleEdgeType', drillholes.edgeType );
			commit( 'updateDrillHoleOffset', drillholes.offset );

			if ( drillholes.edgeType === 'around' ) {
				commit( 'updateEdgeHoles', drillholes.edges );
			}
		},
		async resetDrillHoles( { commit, dispatch } ): Promise<void> {
			commit( 'updateDrillOption', 'none' );
			commit( 'updateDrillHoleEdgeType', 'corners' );
		},
	},
};
